import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Avatar,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from '../../api/axios';

const SpecialistsManager = () => {
  const [specialists, setSpecialists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    specialization: '',
    bio: '',
    phone: '',
    email: '',
    photo: '',
    order: 0,
  });

  useEffect(() => {
    fetchSpecialists();
  }, []);

  const fetchSpecialists = async () => {
    try {
      const response = await axios.get('/specialists');
      setSpecialists(response.data);
    } catch (error) {
      console.error('Ошибка загрузки специалистов:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки специалистов', severity: 'error' });
    }
  };

  const handleOpen = (specialist = null) => {
    if (specialist) {
      setEditingId(specialist._id);
      setFormData(specialist);
      setPhotoPreview(specialist.photo || '');
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        position: '',
        specialization: '',
        bio: '',
        phone: '',
        email: '',
        photo: '',
        order: 0,
      });
      setPhotoPreview('');
    }
    setPhotoFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setPhotoFile(null);
    setPhotoPreview('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      // Создаем превью
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      let photoUrl = formData.photo;

      // Если выбрано новое фото, загружаем его
      if (photoFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', photoFile);

        const uploadResponse = await axios.post('/upload', uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        photoUrl = uploadResponse.data.url;
      }

      const dataToSend = {
        ...formData,
        photo: photoUrl,
      };

      if (editingId) {
        await axios.put(`/specialists/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setSnackbar({ open: true, message: 'Специалист успешно обновлен', severity: 'success' });
      } else {
        await axios.post('/specialists', dataToSend, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setSnackbar({ open: true, message: 'Специалист успешно добавлен', severity: 'success' });
      }
      fetchSpecialists();
      handleClose();
    } catch (error) {
      console.error('Ошибка сохранения специалиста:', error);
      setSnackbar({ 
        open: true, 
        message: `Ошибка: ${error.response?.data?.message || error.message}`, 
        severity: 'error' 
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого специалиста?')) {
      try {
        await axios.delete(`/specialists/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setSnackbar({ open: true, message: 'Специалист успешно удален', severity: 'success' });
        fetchSpecialists();
      } catch (error) {
        console.error('Ошибка удаления специалиста:', error);
        setSnackbar({ open: true, message: 'Ошибка удаления специалиста', severity: 'error' });
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Управление специалистами</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Добавить специалиста
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Фото</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Должность</TableCell>
              <TableCell>Специализация</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {specialists.map((specialist) => (
              <TableRow key={specialist._id}>
                <TableCell>
                  <Avatar src={specialist.photo} alt={specialist.name} />
                </TableCell>
                <TableCell>{specialist.name}</TableCell>
                <TableCell>{specialist.position}</TableCell>
                <TableCell>{specialist.specialization}</TableCell>
                <TableCell>{specialist.phone}</TableCell>
                <TableCell>{specialist.email}</TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpen(specialist)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(specialist._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Редактировать специалиста' : 'Добавить специалиста'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Превью фото */}
            {photoPreview && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar
                  src={photoPreview}
                  sx={{ width: 120, height: 120 }}
                  alt="Превью"
                />
              </Box>
            )}

            {/* Загрузка фото */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              {photoFile ? `Выбрано: ${photoFile.name}` : 'Загрузить фото'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handlePhotoChange}
              />
            </Button>

            <TextField
              fullWidth
              label="Имя *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Должность *"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Специализация"
              name="specialization"
              value={formData.specialization}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label="Биография"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Телефон"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label="Порядок отображения"
              name="order"
              type="number"
              value={formData.order}
              onChange={handleChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 10 }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpecialistsManager;
